import React, { useState, useRef, useEffect } from 'react';
import './GlobalHeader.css';
import logo from '../logo.svg';

const GlobalHeader = () => {
  const go = (path) => {
    try {
      const p = (path || '/').replace(/^#\/?/, '/');
      window.history.pushState({}, '', p);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (_) {
      try { window.location.href = path; } catch (_) {}
    }
  };
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef(null);

  const [colorSupport, setColorSupport] = useState(() => {
    try {
      const v = localStorage.getItem('a11yCvd');
      if (v === null) return false;
      return v === '1';
    } catch (_) {
      return document.body.classList.contains('accessibility-on');
    }
  });
  const [lowStim, setLowStim] = useState(() => {
    try {
      const v = localStorage.getItem('a11yLowStim');
      if (v === null) return false;
      return v === '1';
    } catch (_) {
      return document.body.classList.contains('low-stim-on');
    }
  });
  const [bgmEnabled, setBgmEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('bgmEnabled');
      if (v === null) return !document.body.classList.contains('bgm-off');
      return v === '1';
    } catch (_) {
      return !document.body.classList.contains('bgm-off');
    }
  });
  const [sfxEnabled, setSfxEnabled] = useState(() => {
    try {
      const v = localStorage.getItem('sfxEnabled');
      if (v === null) return !document.body.classList.contains('sfx-off');
      return v === '1';
    } catch (_) {
      return !document.body.classList.contains('sfx-off');
    }
  });

  // 放置速度（低/中/高）: 中速が既定（現在の速さ）
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(() => {
    try {
      const v = localStorage.getItem('autoPlaySpeed');
      if (v === 'slow' || v === 'fast' || v === 'normal') return v;
      return 'normal';
    } catch (_) {
      return 'normal';
    }
  });

  useEffect(() => {
    try { document.body.classList.toggle('accessibility-on', colorSupport); } catch(_) {}
    try { localStorage.setItem('a11yCvd', colorSupport ? '1' : '0'); } catch(_) {}
  }, [colorSupport]);

  useEffect(() => {
    try { document.body.classList.toggle('low-stim-on', lowStim); } catch(_) {}
    try { localStorage.setItem('a11yLowStim', lowStim ? '1' : '0'); } catch(_) {}
  }, [lowStim]);

  useEffect(() => {
    try { localStorage.setItem('bgmEnabled', bgmEnabled ? '1' : '0'); } catch(_) {}
    try { window.bgmEnabled = bgmEnabled; } catch(_) {}
    try { document.body.classList.toggle('bgm-off', !bgmEnabled); } catch(_) {}
    // try to pause/play common audio elements
    try {
      const audios = document.querySelectorAll('audio');
      audios.forEach(a => {
        if (bgmEnabled) {
          a.play().catch(()=>{});
        } else {
          a.pause();
        }
      });
    } catch (_) {}
  }, [bgmEnabled]);

  useEffect(() => {
    try { localStorage.setItem('sfxEnabled', sfxEnabled ? '1' : '0'); } catch(_) {}
    try { window.sfxEnabled = sfxEnabled; } catch(_) {}
    try { document.body.classList.toggle('sfx-off', !sfxEnabled); } catch(_) {}
  }, [sfxEnabled]);

  useEffect(() => {
    try { localStorage.setItem('autoPlaySpeed', autoPlaySpeed); } catch (_) {}
    try { window.autoPlaySpeed = autoPlaySpeed; } catch (_) {}
    // ヘッダー操作中に App 側へ即時反映（放置プレイ中の速度変更を効かせる）
    try { window.__setAutoPlaySpeedImmediate && window.__setAutoPlaySpeedImmediate(autoPlaySpeed); } catch (_) {}
  }, [autoPlaySpeed]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!settingsRef.current) return;
      if (!settingsRef.current.contains(e.target)) setShowSettings(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  return (
    <header className="global-header" role="banner">
      <div className="global-header-inner">
        <div className="header-left" onClick={() => go('/')} role="link" tabIndex={0} aria-label="HOME">
          <img src={logo} alt="ガーリックハムスター" className="header-logo" />
          <span className="header-brand-text">ガーリックハムスター</span>
        </div>
        <nav className="header-nav" aria-label="Main navigation">
          <button className="nav-btn" onClick={() => go('/')} aria-label="ホーム"> 
            <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V11.5z"/></svg>
            <span className="nav-label">ホーム</span>
          </button>
          <button className="nav-btn" onClick={() => go('/play')} aria-label="プレイ">
            <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
            </svg>
            <span className="nav-label">プレイ</span>
          </button>
          <button className="nav-btn" onClick={() => go('/how-to')} aria-label="遊び方">
            <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
              <rect x="11" y="9.5" width="2" height="5" rx="1" fill="currentColor" />
              <circle cx="12" cy="7" r="1" fill="currentColor" />
            </svg>
            <span className="nav-label">遊び方</span>
          </button>
          <div className="settings-root">
            <button className="nav-btn" onClick={(e) => { e.stopPropagation(); setShowSettings(s => !s); }} aria-label="設定" aria-haspopup="true" aria-expanded={showSettings}>
              <svg className="nav-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M19.4 12.9a7.8 7.8 0 0 0 0-1.8l2.1-1.6-2-3.4-2.5.7a7.7 7.7 0 0 0-1.5-.9L15 2h-6l-.5 4.1c-.5.2-1 .5-1.5.9l-2.5-.7-2 3.4L4.6 11a7.8 7.8 0 0 0 0 1.8l-2.1 1.6 2 3.4 2.5-.7c.5.4 1 .7 1.5.9L9 22h6l.5-4.1c.5-.2 1-.5 1.5-.9l2.5.7 2-3.4-2.1-1.6zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"/></svg>
              <span className="nav-label">設定</span>
            </button>
            {showSettings && (
              <div className="settings-dropdown" ref={settingsRef} role="menu">
                <div className="settings-panel-title">設定</div>
                <label className="settings-toggle">
                  <span>色覚サポート</span>
                  <input type="checkbox" checked={colorSupport} onChange={(e)=>setColorSupport(e.target.checked)} />
                  <span className="switch" />
                </label>
                <label className="settings-toggle">
                  <span>低刺激</span>
                  <input type="checkbox" checked={lowStim} onChange={(e)=>setLowStim(e.target.checked)} />
                  <span className="switch" />
                </label>
                <label className="settings-toggle">
                  <span>BGM</span>
                  <input
                    type="checkbox"
                    checked={bgmEnabled}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setBgmEnabled(next);
                      // スマホ(Safari/Chrome)はユーザー操作外の play() がブロックされやすいので、
                      // ここ(ユーザー操作中)でApp側へ即時適用を依頼する。
                      try { window.__setBgmEnabledImmediate && window.__setBgmEnabledImmediate(next); } catch (_) {}
                    }}
                  />
                  <span className="switch" />
                </label>
                <label className="settings-toggle">
                  <span>効果音</span>
                  <input type="checkbox" checked={sfxEnabled} onChange={(e)=>setSfxEnabled(e.target.checked)} />
                  <span className="switch" />
                </label>

                <div className="settings-divider" role="separator" />
                <div className="settings-row">
                  <div className="settings-row-label">放置速度</div>
                  <div className="settings-segment" role="group" aria-label="放置速度">
                    <button
                      type="button"
                      className={`settings-seg-btn${autoPlaySpeed === 'slow' ? ' active' : ''}`}
                      aria-pressed={autoPlaySpeed === 'slow'}
                      aria-label="低速"
                      onClick={() => setAutoPlaySpeed('slow')}
                    >
                      低
                    </button>
                    <button
                      type="button"
                      className={`settings-seg-btn${autoPlaySpeed === 'normal' ? ' active' : ''}`}
                      aria-pressed={autoPlaySpeed === 'normal'}
                      aria-label="中速"
                      onClick={() => setAutoPlaySpeed('normal')}
                    >
                      中
                    </button>
                    <button
                      type="button"
                      className={`settings-seg-btn${autoPlaySpeed === 'fast' ? ' active' : ''}`}
                      aria-pressed={autoPlaySpeed === 'fast'}
                      aria-label="高速"
                      onClick={() => setAutoPlaySpeed('fast')}
                    >
                      高
                    </button>
                  </div>
                </div>
                <div style={{ height: 8 }} />
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default GlobalHeader;
